import type { AttackKind } from './simulation'
import { runtimePublicPath } from './runtimePublicPath'

const configuredPublicBase = (import.meta.env.VITE_ELYON_PUBLIC_BASE as string | undefined)?.replace(/\/$/, '')

/**
 * Public Elyon content root. The integrated game lives under /elyon, while a
 * standalone Fight preview can point this at the deployed Elyon origin with
 * VITE_ELYON_PUBLIC_BASE. Paths remain canonical Elyon paths; Fight never owns
 * or copies these samples.
 */
export const ELYON_PUBLIC_BASE = configuredPublicBase || runtimePublicPath('/elyon')
export const ELYON_SHARED_PRESENTATION_MANIFEST = '/assets/manifests/shared-presentation-v1.json'

export function elyonPublicAsset(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${ELYON_PUBLIC_BASE}${normalized}`
}

export const ELYON_AUDIO = {
  combatHit: '/assets/audio/combat/hit.mp3',
  combatSwing: '/assets/audio/combat/swing.mp3',
  combatShockwave: '/assets/audio/combat/shockwave.mp3',
  combatCrit: '/assets/audio/combat/crit-accent.mp3',
  combatHurt: '/assets/audio/combat/player-hurt.mp3',
  combatVictory: '/assets/audio/combat/victory.mp3',
  combatDefeat: '/assets/audio/combat/defeat.mp3',
  combatBossEntrance: '/assets/audio/combat/boss-entrance.mp3',
  musicCombat: '/assets/audio/music/combat-loop.mp3',
} as const

/**
 * Authored particle sprites published by the Elyon shared content fabric.
 * Their source/provenance is owned upstream; Fight only binds semantic combat
 * events to stable canonical URLs. The integrated Elyon build materializes
 * Arena-owned extensions into /assets without allowing them to replace an
 * Elyon-authored key.
 */
export const ELYON_VFX = {
  hit: '/assets/vfx/particles/spark_01.png',
  heavy: '/assets/vfx/particles/spark_07.png',
  special: '/assets/vfx/particles/magic_03.png',
  throw: '/assets/vfx/particles/magic_01.png',
  block: '/assets/vfx/particles/light_01.png',
  parry: '/assets/vfx/particles/trace_04.png',
  guardBreak: '/assets/vfx/particles/flare_01.png',
  status: '/assets/vfx/particles/light_03.png',
  downed: '/assets/vfx/particles/smoke_03.png',
} as const

export function attackAudioPath(kind: AttackKind | null): string {
  if (kind === 'special') return ELYON_AUDIO.combatShockwave
  if (kind === 'heavy' || kind === 'throw') return ELYON_AUDIO.combatCrit
  return ELYON_AUDIO.combatSwing
}

export function audioUrl(path: string): string {
  return elyonPublicAsset(path)
}

export function vfxUrl(path: string): string {
  return elyonPublicAsset(path)
}

export function sharedPresentationManifestUrl(): string {
  return elyonPublicAsset(ELYON_SHARED_PRESENTATION_MANIFEST)
}
